/**
 * Dev-only "Who Am I" Debug Panel
 *
 * Displays current auth state for debugging workspace switching.
 * ONLY rendered in development mode.
 *
 * Shows:
 * - Client auth state (from Redux)
 * - Decoded JWT workspaceId (from raw token)
 * - Server verification (from /api/v1/auth/whoami)
 * - Match indicator between all three
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bug, ChevronDown, ChevronUp, RefreshCw, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';
import { useAuth } from '../hooks/useAuth';
import { useBootContext } from '../hooks/useBootContext';

interface WhoAmIResponse {
  userId: string;
  email: string;
  workspaceId: string;
  scope: string[]; // Central uses "scope" (array)
  role: string | null;
  tier: string | null;
  tokenIssuedAt: string;
  tokenExpiresAt: string;
}

interface DecodedToken {
  sub?: string;
  email?: string;
  workspaceId?: string;
  workspace_id?: string;
  scope?: string[]; // Central uses "scope" (array)
  scopes?: string[]; // Legacy fallback
  role?: string;
  tier?: string;
  iat?: number;
  exp?: number;
}

// Only show in development
const isDev = import.meta.env.DEV;

/**
 * Decodes a JWT token without verifying signature.
 * For debugging display only - backend MUST verify.
 */
function decodeJwt(token: string): DecodedToken | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    if (!payload) return null;
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

export function DevWhoAmI() {
  const { token } = useAuth();
  const bootContext = useBootContext();
  const [isExpanded, setIsExpanded] = useState(false);

  // Decode token to get raw claims
  const decodedToken = useMemo(() => {
    if (!token) return null;
    return decodeJwt(token);
  }, [token]);

  // Get workspaceId from decoded token (handle both camelCase and snake_case)
  const tokenWorkspaceId =
    decodedToken?.workspaceId || decodedToken?.workspace_id;

  // Fetch from backend to verify token is working
  const {
    data: whoami,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<WhoAmIResponse>({
    queryKey: ['whoami'],
    queryFn: async () => {
      const response = await fetch('/api/v1/auth/whoami', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`${response.status}: ${response.statusText}`);
      }
      return response.json();
    },
    enabled: !!token && isExpanded,
    staleTime: 0, // Always refetch when expanded
    retry: false,
  });

  // Don't render in production
  if (!isDev) {
    return null;
  }

  const bootWorkspaceId = bootContext?.currentWorkspace.id;

  // Check if all three workspaceIds match
  const allMatch =
    tokenWorkspaceId &&
    bootWorkspaceId &&
    whoami?.workspaceId &&
    tokenWorkspaceId === bootWorkspaceId &&
    bootWorkspaceId === whoami.workspaceId;

  const clientServerMatch =
    bootWorkspaceId && whoami?.workspaceId && bootWorkspaceId === whoami.workspaceId;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Collapsed button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={clsx(
          'flex items-center px-3 py-2 text-sm font-medium rounded-lg shadow-lg transition-colors',
          'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 border border-yellow-300',
          isExpanded && 'rounded-b-none'
        )}
      >
        <Bug className="h-4 w-4 mr-2" />
        Who Am I
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 ml-2" />
        ) : (
          <ChevronUp className="h-4 w-4 ml-2" />
        )}
      </button>

      {/* Expanded panel */}
      {isExpanded && (
        <div className="bg-yellow-50 border border-t-0 border-yellow-300 rounded-b-lg shadow-lg p-4 w-96 max-h-[70vh] overflow-y-auto">
          {/* Decoded Token Claims */}
          <div className="mb-3">
            <h4 className="text-xs font-semibold text-yellow-800 uppercase mb-2">
              Decoded JWT Claims
            </h4>
            {decodedToken ? (
              <dl className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <dt className="text-yellow-700">Subject (sub):</dt>
                  <dd className="font-mono text-yellow-900 truncate max-w-[180px]">
                    {decodedToken.sub ?? 'N/A'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-yellow-700">Workspace ID:</dt>
                  <dd className="font-mono text-yellow-900 truncate max-w-[180px]">
                    {tokenWorkspaceId ?? 'N/A'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-yellow-700">Email:</dt>
                  <dd className="font-mono text-yellow-900">
                    {decodedToken.email ?? 'N/A'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-yellow-700">Role:</dt>
                  <dd className="font-mono text-yellow-900">
                    {decodedToken.role ?? 'N/A'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-yellow-700">Issued:</dt>
                  <dd className="font-mono text-yellow-900 text-[10px]">
                    {decodedToken.iat
                      ? new Date(decodedToken.iat * 1000).toLocaleTimeString()
                      : 'N/A'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-yellow-700">Expires:</dt>
                  <dd className="font-mono text-yellow-900 text-[10px]">
                    {decodedToken.exp
                      ? new Date(decodedToken.exp * 1000).toLocaleTimeString()
                      : 'N/A'}
                  </dd>
                </div>
              </dl>
            ) : (
              <p className="text-xs text-yellow-600">No token to decode</p>
            )}
          </div>

          <hr className="border-yellow-200 my-3" />

          {/* Boot Context (from switch-context) */}
          <div className="mb-3">
            <h4 className="text-xs font-semibold text-yellow-800 uppercase mb-2">
              Boot Context (switch-context)
            </h4>
            <dl className="space-y-1 text-xs">
              <div className="flex justify-between">
                <dt className="text-yellow-700">Email:</dt>
                <dd className="font-mono text-yellow-900">{bootContext?.user.email ?? 'N/A'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-yellow-700">Workspace:</dt>
                <dd className="font-mono text-yellow-900 truncate max-w-[180px]">
                  {bootContext?.currentWorkspace.id ?? 'N/A'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-yellow-700">WS Name:</dt>
                <dd className="font-mono text-yellow-900">{bootContext?.currentWorkspace.name ?? 'N/A'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-yellow-700">Role:</dt>
                <dd className="font-mono text-yellow-900">{bootContext?.currentWorkspace.role ?? 'N/A'}</dd>
              </div>
              <div className="flex flex-col">
                <dt className="text-yellow-700">Entitlements:</dt>
                <dd className="font-mono text-yellow-900 text-[10px] mt-1 break-all">
                  {bootContext?.entitlementsEffective?.join(', ') || 'N/A'}
                </dd>
              </div>
            </dl>
          </div>

          <hr className="border-yellow-200 my-3" />

          {/* Server verification */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-yellow-800 uppercase">
                Server Verification (/whoami)
              </h4>
              <button
                onClick={() => refetch()}
                disabled={isLoading}
                className="p-1 hover:bg-yellow-200 rounded"
                title="Refresh"
              >
                {isLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin text-yellow-600" />
                ) : (
                  <RefreshCw className="h-3 w-3 text-yellow-600" />
                )}
              </button>
            </div>

            {isError ? (
              <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
                {error instanceof Error ? error.message : 'Failed to verify'}
              </div>
            ) : whoami ? (
              <dl className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <dt className="text-yellow-700">User ID:</dt>
                  <dd className="font-mono text-yellow-900 truncate max-w-[180px]">
                    {whoami.userId}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-yellow-700">Email:</dt>
                  <dd className="font-mono text-yellow-900">{whoami.email}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-yellow-700">Workspace:</dt>
                  <dd className="font-mono text-yellow-900 truncate max-w-[180px]">
                    {whoami.workspaceId}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-yellow-700">Role:</dt>
                  <dd className="font-mono text-yellow-900">{whoami.role ?? 'N/A'}</dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-yellow-700">Scope:</dt>
                  <dd className="font-mono text-yellow-900 text-[10px] mt-1 break-all">
                    {whoami.scope?.join(', ') || 'N/A'}
                  </dd>
                </div>
              </dl>
            ) : (
              <p className="text-xs text-yellow-600">
                {isLoading ? 'Loading...' : 'Click refresh to verify'}
              </p>
            )}
          </div>

          {/* Match indicators */}
          {(decodedToken || whoami) && bootContext && (
            <div className="mt-3 space-y-2">
              {/* Token vs Boot Context match */}
              {decodedToken && (
                <div
                  className={clsx(
                    'p-2 rounded text-xs font-medium text-center',
                    tokenWorkspaceId === bootWorkspaceId
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  )}
                >
                  {tokenWorkspaceId === bootWorkspaceId
                    ? '✓ JWT & Boot Context workspace match'
                    : '✗ JWT & Boot Context workspace MISMATCH'}
                </div>
              )}

              {/* Client vs Server match */}
              {whoami && (
                <div
                  className={clsx(
                    'p-2 rounded text-xs font-medium text-center',
                    clientServerMatch
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  )}
                >
                  {clientServerMatch
                    ? '✓ Client & Server workspace match'
                    : '✗ Client & Server workspace MISMATCH'}
                </div>
              )}

              {/* All three match */}
              {whoami && decodedToken && (
                <div
                  className={clsx(
                    'p-2 rounded text-xs font-bold text-center',
                    allMatch
                      ? 'bg-green-200 text-green-900'
                      : 'bg-orange-100 text-orange-800'
                  )}
                >
                  {allMatch
                    ? '✓ ALL THREE MATCH'
                    : '⚠ Partial mismatch - check above'}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
